/**
 * Central auth-session helper.
 *
 * Single source of truth for the JWT that keeps a user logged in. Handles
 * persistence across reloads, client-side expiry detection (honouring the
 * token's `exp` claim) and clean teardown on logout / expiry.
 */
import { clearAllLocalWorkflow } from "../mock/localWorkflowStore";

const TOKEN_KEY = "onepass_token";

/** Decode the JWT payload without verifying the signature. */
export function getTokenPayload(token = getRawToken()) {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    // base64url -> base64
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/** True if the token is missing, malformed or past its `exp` (with small skew). */
export function isTokenExpired(token = getRawToken()) {
  const payload = getTokenPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  // 10s clock-skew allowance; exp is in seconds since epoch.
  return Date.now() >= payload.exp * 1000 - 10_000;
}

/** Raw value straight from storage (no expiry check). Internal use. */
function getRawToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * The active token, or null. If a token exists but has expired it is purged
 * so the rest of the app consistently sees the user as logged out.
 */
export function getToken() {
  const token = getRawToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    clearSession();
    return null;
  }
  return token;
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function isAuthenticated() {
  return getToken() !== null;
}

/** Remove the token and any per-user local workflow data. No navigation. */
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  clearAllLocalWorkflow();
}

/**
 * Log the user out and (by default) send them to the login page.
 * Uses a hard redirect so it can be called from non-React code such as the
 * axios interceptors. Guards against redirect loops when already on an auth page.
 */
export function logout({ redirect = true } = {}) {
  clearSession();
  if (!redirect) return;
  const path = window.location.pathname;
  if (path !== "/build/login" && path !== "/build/register") {
    window.location.href = "/build/login";
  }
}
